import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Map as MapIcon, Pencil, Save, X, Globe, Code2 } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DepartmentMap } from "../components/DepartmentMap";
import { EmbedDialog } from "../components/EmbedDialog";

const toggleCode = (code, auto, added, removed) => {
  const covered = (auto.includes(code) && !removed.includes(code)) || added.includes(code);
  if (covered) return added.includes(code) ? [added.filter((c) => c !== code), removed] : [added, [...removed, code]];
  return removed.includes(code) ? [added, removed.filter((c) => c !== code)] : [[...added, code], removed];
};

const previewDepts = (data, added, removed) => {
  const base = Object.fromEntries((data.departments || []).filter((d) => !d.manual).map((d) => [d.code, d]));
  const codes = new Set([...(data.auto || []), ...added].filter((c) => !removed.includes(c)));
  return [...codes].sort().map((c) => base[c] || { code: c, name: data.all?.[c] || c, sites: 0, agents: 0, manual: true });
};

function DeptList({ departments, isAdmin, selected, onSelect }) {
  if (!departments.length) return <p className="text-sm text-slate-500" data-testid="coverage-empty">Aucun département pour l&apos;instant. Renseignez le code postal de vos sites.</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="coverage-list">
      {departments.map((d) => (
        <li key={d.code}>
          <button data-testid={`coverage-item-${d.code}`} onClick={() => onSelect(selected === d.code ? null : d.code)}
                  className={`w-full flex items-center gap-3 px-2 py-2 text-left text-sm rounded hover:bg-slate-50 ${selected === d.code ? "bg-sky-50" : ""}`}>
            <span className="font-mono text-xs font-bold w-9 text-center rounded py-0.5 text-white" style={{ backgroundColor: "var(--brand-primary)" }}>{d.code}</span>
            <span className="flex-1 font-medium">{d.name}{d.manual && <span className="ml-1 text-[10px] text-slate-400">(manuel)</span>}</span>
            <span className="text-xs text-slate-500">{d.sites} site{d.sites > 1 ? "s" : ""}{isAdmin && d.agents != null ? ` · ${d.agents} ag.` : ""}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function AdminBar({ edit, setEdit, onSave, onCancel, publicOn, setPublicOn, onEmbed }) {
  if (!edit) return (
    <div className="flex gap-2">
      <button data-testid="coverage-embed-btn" onClick={onEmbed} className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md text-white hover:opacity-90" style={{ backgroundColor: "var(--brand-primary)" }}><Code2 className="h-4 w-4" />Intégrer à mon site</button>
      <button data-testid="coverage-edit-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50"><Pencil className="h-4 w-4" />Ajuster les départements</button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-slate-300 rounded-md bg-white">
        <input data-testid="coverage-public-toggle" type="checkbox" checked={publicOn} onChange={(e) => setPublicOn(e.target.checked)} />
        <Globe className="h-4 w-4 text-slate-500" />Carte publique (/carte-publique)
      </label>
      <button data-testid="coverage-cancel-btn" onClick={onCancel} className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50"><X className="h-4 w-4" />Annuler</button>
      <button data-testid="coverage-save-btn" onClick={onSave} className="inline-flex items-center gap-1 text-sm font-semibold text-white px-3 py-2 rounded-md hover:opacity-90" style={{ backgroundColor: "var(--brand-primary)" }}><Save className="h-4 w-4" />Enregistrer</button>
    </div>
  );
}

export default function CoverageMap() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [publicOn, setPublicOn] = useState(false);
  const [selected, setSelected] = useState(null);
  const [embedOpen, setEmbedOpen] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await http.get("/coverage");
    setData(d); setAdded(d.added || []); setRemoved(d.removed || []); setPublicOn(!!d.public_enabled);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await http.put("/coverage", { added, removed, public_enabled: publicOn });
      toast.success("Zone d'intervention enregistrée"); setEdit(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const cancel = () => { setEdit(false); setAdded(data.added || []); setRemoved(data.removed || []); setPublicOn(!!data.public_enabled); };
  const onToggle = (c) => { const [a, r] = toggleCode(c, data.auto || [], added, removed); setAdded(a); setRemoved(r); };

  if (!data) return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  const depts = edit ? previewDepts(data, added, removed) : data.departments;
  const overseas = depts.filter((d) => d.code.length === 3);

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="coverage-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3"><MapIcon className="h-8 w-8 text-sky-600" />Zone d&apos;intervention</h1>
          <p className="text-sm text-slate-600 mt-1" data-testid="coverage-count">{depts.length} département{depts.length > 1 ? "s" : ""} couvert{depts.length > 1 ? "s" : ""} — déterminés à partir du code postal des sites.</p>
        </div>
        {isAdmin && <AdminBar edit={edit} setEdit={setEdit} onSave={save} onCancel={cancel} publicOn={publicOn} setPublicOn={setPublicOn} onEmbed={() => setEmbedOpen(true)} />}
      </div>
      {embedOpen && <EmbedDialog data={data} onClose={() => setEmbedOpen(false)} onSaved={load} />}
      {edit && <div className="mb-4 text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-4 py-2" data-testid="coverage-edit-hint">Mode ajustement : cliquez sur un département pour l&apos;ajouter ou le retirer de la carte.</div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-200 bg-white rounded-md p-4">
          <DepartmentMap departments={depts} showAgents={isAdmin} editMode={edit} onToggle={onToggle} selected={selected} onSelect={setSelected} />
          {overseas.length > 0 && <div className="mt-3 text-xs text-slate-600" data-testid="coverage-overseas">Outre-mer : {overseas.map((d) => `${d.name} (${d.code})`).join(", ")}</div>}
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4 max-h-[680px] overflow-auto">
          <h3 className="font-display text-lg font-bold mb-3">Départements</h3>
          <DeptList departments={depts} isAdmin={isAdmin} selected={selected} onSelect={setSelected} />
        </div>
      </div>
    </div>
  );
}
